import React, { useCallback, useEffect, useState } from "react";
import {
  Button,
  DatePicker,
  Drawer,
  Dropdown,
  Form,
  Input,
  InputNumber,
  Modal,
  Select,
  Tag,
  Tooltip,
  Typography
} from "antd";
import {
  EditOutlined,
  HistoryOutlined,
  OrderedListOutlined,
  LockOutlined,
  MoreOutlined,
  PlusOutlined,
  RollbackOutlined
} from "@ant-design/icons";
import dayjs from "dayjs";
import { useTranslation } from "react-i18next";
import { formatCents, formatDate } from "../shared/money";
import Amount from "../shared/Amount";
import FilterToolbar from "../shared/FilterToolbar";
import ResponsiveTable from "../../../../design-system/ResponsiveTable";
import { useIsMobile } from "../../../../design-system/useMediaQuery";
import { Success, Error as ErrorMsg } from "../../../../commons/Messages/Messages";
import { errorText, listEntries, listProjects, reverseEntry } from "./api";
import {
  ORIGINS,
  ORIGIN_COLORS,
  ORIGIN_LABELS,
  TAX_KIND_LABELS,
  linesSummary,
  sumCredit,
  sumDebit
} from "./labels";
import { filterAccountOption } from "./useAccounts";
import EntryModal from "./EntryModal";
import AuditDrawer from "./AuditDrawer";

// Entry lines stay a real debit/credit grid on every screen (scrolls horizontally on phones).
const LinesTable = ({ lines, accounts, projectsById, t }) => (
  <ResponsiveTable
    mobileLayout="table"
    className="acct-grid"
    size="small"
    pagination={false}
    rowKey={(_, i) => i}
    dataSource={lines}
    columns={[
      {
        title: t("Account"),
        dataIndex: "account",
        render: n => (
          <span>
            <strong>{n}</strong> {accounts.byNumber.get(n)?.name || ""}
          </span>
        )
      },
      {
        title: t("Debit (Soll)"),
        dataIndex: "debit",
        align: "end",
        render: v => (v ? <Amount cents={v} /> : "")
      },
      {
        title: t("Credit (Haben)"),
        dataIndex: "credit",
        align: "end",
        render: v => (v ? <Amount cents={v} /> : "")
      },
      {
        title: t("Tax"),
        key: "tax",
        render: (_, l) =>
          l.taxKind && l.taxKind !== "none" ? (
            <span style={{ whiteSpace: "nowrap" }}>
              {t(TAX_KIND_LABELS[l.taxKind])} {l.taxRate} % ·{" "}
              <span className="acct-num">{formatCents(l.taxAmount)}</span>
            </span>
          ) : (
            ""
          )
      },
      {
        title: t("Cost centre (project)"),
        dataIndex: "costCentre",
        render: id => (id ? projectsById.get(String(id)) || String(id) : "")
      },
      { title: t("Text"), dataIndex: "text" }
    ]}
  />
);

const JournalTab = ({ accounts }) => {
  const { t } = useTranslation("accounting");
  const isMobile = useIsMobile();
  const [filters, setFilters] = useState({
    year: new Date().getFullYear(),
    period: null,
    account: null,
    origin: null,
    q: ""
  });
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(50);
  const [data, setData] = useState({ entries: [], total: 0 });
  const [loading, setLoading] = useState(false);
  const [projects, setProjects] = useState([]);
  const [modal, setModal] = useState({ open: false, entry: null });
  const [storno, setStorno] = useState(null);
  const [stornoForm] = Form.useForm();
  const [reversing, setReversing] = useState(false);
  const [audit, setAudit] = useState(null);
  // Phones show entries as cards; their lines open in a drawer instead of an expanded row.
  const [linesOf, setLinesOf] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const body = { page, limit };
      if (filters.period) body.period = filters.period;
      else if (filters.year) body.year = filters.year;
      if (filters.account) body.account = filters.account;
      if (filters.origin) body.origin = filters.origin;
      if (filters.q) body.q = filters.q;
      const res = await listEntries(body);
      setData({ entries: res.data.entries || [], total: res.data.total || 0 });
    } catch (err) {
      ErrorMsg(errorText(err, t("Could not load the journal")));
    } finally {
      setLoading(false);
    }
  }, [filters, page, limit, t]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    listProjects()
      .then(setProjects)
      .catch(() => setProjects([]));
  }, []);

  const projectsById = new Map(
    projects.map(p => [String(p._id), p.projectName || p.name || String(p._id)])
  );

  const update = patch => {
    setPage(1);
    setFilters(prev => ({ ...prev, ...patch }));
  };

  const doStorno = async () => {
    const values = await stornoForm.validateFields().catch(() => null);
    if (!values) return;
    setReversing(true);
    try {
      await reverseEntry(storno._id, {
        reason: values.reason || "",
        date: values.date ? values.date.format("YYYY-MM-DD") : undefined
      });
      Success(t("Entry reversed"));
      setStorno(null);
      stornoForm.resetFields();
      load();
    } catch (err) {
      ErrorMsg(errorText(err, t("Could not reverse the entry")));
    } finally {
      setReversing(false);
    }
  };

  const lockText = e =>
    t("Locked (Festschreibung) on {{date}}", { date: dayjs(e.lockedAt).format("DD.MM.YYYY") });

  const columns = [
    {
      title: t("No."),
      key: "no",
      width: 90,
      render: (_, e) => (
        <span className="acct-num">{`${e.entryNo}/${String(e.year).slice(-2)}`}</span>
      )
    },
    { title: t("Date"), dataIndex: "date", width: 100, render: formatDate },
    {
      title: t("Document no. (Belegnummer)"),
      dataIndex: "belegRef",
      width: 150,
      ellipsis: !isMobile
    },
    {
      title: t("Booking text (Buchungstext)"),
      dataIndex: "text",
      ellipsis: !isMobile,
      mobile: "title",
      render: (text, e) => (
        <span className="acct-wrap">
          {text}
          {e.reversedBy ? (
            <Tag color="red" style={{ marginInlineStart: 6 }}>
              {t("reversed")}
            </Tag>
          ) : null}
        </span>
      )
    },
    {
      title: t("Account → contra account"),
      key: "lines",
      width: 180,
      render: (_, e) => <Typography.Text code>{linesSummary(e.lines)}</Typography.Text>
    },
    {
      title: t("Debit (Soll)"),
      key: "debit",
      align: "end",
      width: 120,
      // A posted entry is balanced, so on a card its debit total is the entry amount.
      mobile: "subtitle",
      render: (_, e) => <Amount cents={sumDebit(e.lines)} large={isMobile} />
    },
    {
      title: t("Credit (Haben)"),
      key: "credit",
      align: "end",
      width: 120,
      mobile: "hidden",
      render: (_, e) => <Amount cents={sumCredit(e.lines)} />
    },
    {
      title: t("Origin"),
      dataIndex: "origin",
      width: 120,
      render: o => <Tag color={ORIGIN_COLORS[o]}>{t(ORIGIN_LABELS[o] || o)}</Tag>
    },
    {
      key: "lock",
      width: 40,
      mobileLabel: t("Status"),
      render: (_, e) =>
        e.lockedAt ? (
          isMobile ? (
            <span>
              <LockOutlined /> {lockText(e)}
            </span>
          ) : (
            <Tooltip title={lockText(e)} trigger={["hover", "click"]}>
              <LockOutlined aria-label={lockText(e)} />
            </Tooltip>
          )
        ) : isMobile ? (
          t("Open")
        ) : null
    },
    {
      key: "actions",
      width: 50,
      render: (_, e) => (
        <>
          {isMobile ? (
            <Button size="small" icon={<OrderedListOutlined />} onClick={() => setLinesOf(e)}>
              {t("Lines")}
            </Button>
          ) : null}
          <Dropdown
            trigger={["click"]}
            menu={{
              items: [
                {
                  key: "edit",
                  icon: <EditOutlined />,
                  label: t("Edit"),
                  disabled: Boolean(e.lockedAt),
                  onClick: () => setModal({ open: true, entry: e })
                },
                {
                  key: "storno",
                  icon: <RollbackOutlined />,
                  label: t("Reverse (Storno)"),
                  disabled: Boolean(e.reversedBy),
                  onClick: () => setStorno(e)
                },
                {
                  key: "audit",
                  icon: <HistoryOutlined />,
                  label: t("Show audit trail"),
                  onClick: () => setAudit(e)
                }
              ]
            }}
          >
            <Button type="text" icon={<MoreOutlined />} />
          </Dropdown>
        </>
      )
    }
  ];

  const activeFilters =
    (filters.period ? 1 : 0) +
    (filters.account ? 1 : 0) +
    (filters.origin ? 1 : 0) +
    (!filters.period && filters.year !== new Date().getFullYear() ? 1 : 0);

  return (
    <div>
      <FilterToolbar
        search={
          <Input.Search
            placeholder={t("Search text or document no.")}
            allowClear
            onSearch={q => update({ q })}
          />
        }
        activeCount={activeFilters}
        filters={
          <>
            <DatePicker
              picker="month"
              placeholder={t("Period (month)")}
              value={filters.period ? dayjs(filters.period, "YYYY-MM") : null}
              onChange={v => update({ period: v ? v.format("YYYY-MM") : null })}
            />
            <InputNumber
              placeholder={t("Year")}
              value={filters.year}
              onChange={v => update({ year: v || null })}
              min={2000}
              max={2100}
              disabled={Boolean(filters.period)}
            />
            <Select
              showSearch
              allowClear
              placeholder={t("Account")}
              value={filters.account}
              onChange={v => update({ account: v || null })}
              options={accounts.options}
              filterOption={filterAccountOption}
            />
            <Select
              allowClear
              placeholder={t("Origin")}
              value={filters.origin}
              onChange={v => update({ origin: v || null })}
              options={ORIGINS.map(o => ({ value: o, label: t(ORIGIN_LABELS[o]) }))}
            />
          </>
        }
        actions={
          <Button
            type="primary"
            icon={<PlusOutlined />}
            onClick={() => setModal({ open: true, entry: null })}
          >
            {t("New manual booking")}
          </Button>
        }
      />
      <ResponsiveTable
        rowKey="_id"
        size="small"
        loading={loading}
        columns={columns}
        dataSource={data.entries}
        scroll={{ x: 1100 }}
        expandable={
          isMobile
            ? undefined
            : {
                expandedRowRender: e => (
                  <LinesTable
                    lines={e.lines}
                    accounts={accounts}
                    projectsById={projectsById}
                    t={t}
                  />
                )
              }
        }
        pagination={{
          current: page,
          pageSize: limit,
          total: data.total,
          showSizeChanger: true,
          showTotal: total => t("{{count}} entries", { count: total }),
          onChange: (p, size) => {
            setPage(size !== limit ? 1 : p);
            setLimit(size);
          }
        }}
      />

      <Drawer
        rootClassName="acct-module"
        open={Boolean(linesOf)}
        onClose={() => setLinesOf(null)}
        width={isMobile ? "100%" : 720}
        title={linesOf ? `${linesOf.entryNo}/${linesOf.year} · ${linesOf.text || ""}` : t("Lines")}
      >
        {linesOf ? (
          <LinesTable lines={linesOf.lines} accounts={accounts} projectsById={projectsById} t={t} />
        ) : null}
      </Drawer>

      <EntryModal
        open={modal.open}
        entry={modal.entry}
        accounts={accounts}
        projects={projects}
        onClose={() => setModal({ open: false, entry: null })}
        onSaved={() => {
          setModal({ open: false, entry: null });
          load();
        }}
      />

      <Modal
        rootClassName="acct-module"
        open={Boolean(storno)}
        title={storno ? t("Reverse entry {{no}}", { no: `${storno.entryNo}/${storno.year}` }) : ""}
        onCancel={() => setStorno(null)}
        onOk={doStorno}
        okText={t("Reverse (Storno)")}
        okButtonProps={{ danger: true, loading: reversing }}
        destroyOnClose
      >
        <Typography.Paragraph type="secondary">
          {t(
            "A reversal posts a mirrored entry in an open period and links both. The original stays in the books; nothing is deleted."
          )}
        </Typography.Paragraph>
        <Form form={stornoForm} layout="vertical" initialValues={{ date: dayjs(), reason: "" }}>
          <Form.Item
            name="date"
            label={t("Reversal date")}
            rules={[{ required: true, message: t("Required") }]}
          >
            <DatePicker style={{ width: "100%" }} format="DD.MM.YYYY" />
          </Form.Item>
          <Form.Item
            name="reason"
            label={t("Reason")}
            rules={[{ required: true, message: t("Please give a reason for the reversal") }]}
          >
            <Input.TextArea rows={3} />
          </Form.Item>
        </Form>
      </Modal>

      <AuditDrawer
        open={Boolean(audit)}
        collection="JournalEntry"
        id={audit?._id}
        title={
          audit ? t("Audit trail of entry {{no}}", { no: `${audit.entryNo}/${audit.year}` }) : null
        }
        onClose={() => setAudit(null)}
      />
    </div>
  );
};

export default JournalTab;
